<script setup>
import iconTarget from '@manseImages/nangman-naming/icon-target.svg?url';
import iconMy from '@manseImages/nangman-naming/icon-my.svg?url';
import { useScrollLock } from '@/composables/useScrollLock';

const props = defineProps({
  isVisible: { type: Boolean, default: false },
  /** 내 사주 — 시/일/월/년 천간지지 + 핵심 오행 태그 */
  saju: {
    type: Object,
    default: () => ({ pillars: [], tags: [] }),
  },
});

const emit = defineEmits(['close']);

/** 팝업 노출 중 부모 스크롤 잠금 (PC/모바일 자동 분기) */
useScrollLock(computed(() => props.isVisible));

const close = () => emit('close');
</script>

<template>
  <Teleport to="body">
    <div v-if="isVisible" class="pop-criteria-backdrop" @click.self="close">
      <div class="pop-criteria">
        <!-- 헤더 -->
        <div class="pop-criteria__header">
          <div class="pop-criteria__title-wrap">
            <img :src="iconTarget" alt="" class="pop-criteria__title-icon" width="24" height="24" />
            <h3 class="pop-criteria__title">분석 기준</h3>
            <span class="pop-criteria__tag">이 기준으로 이름을 분석했어요</span>
          </div>
          <button type="button" class="pop-criteria__close" @click="close" aria-label="닫기">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 6L18 18M18 6L6 18"
                stroke="#333"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>

        <!-- 내 사주 — 분석 팝업은 이 섹션이 유일한 콘텐츠라, 데이터가 없으면 빈 팝업 대신 안내 문구 표시 -->
        <section v-if="saju.pillars && saju.pillars.length" class="pop-criteria__section">
          <div class="pop-criteria__section-head">
            <img :src="iconMy" alt="" class="pop-criteria__section-icon" width="18" height="18" />
            <span class="pop-criteria__section-title">내 사주</span>
          </div>
          <div class="pop-criteria__pillars-labels">
            <span v-for="p in saju.pillars" :key="'l-' + p.label" class="pop-criteria__pillar-label">
              {{ p.label }}
            </span>
          </div>
          <div class="pop-criteria__pillars">
            <div v-for="p in saju.pillars" :key="'p-' + p.label" class="pop-criteria__pillar">
              <div class="pop-criteria__pillar-chars">
                <span :style="{ color: p.stemColor }">{{ p.stem }}</span>
                <span :style="{ color: p.branchColor }">{{ p.branch }}</span>
              </div>
              <span class="pop-criteria__pillar-kor">{{ p.kor }}</span>
            </div>
          </div>
          <div v-if="saju.tags && saju.tags.length" class="pop-criteria__saju-tags">
            <span v-for="t in saju.tags" :key="t.label" class="pop-criteria__saju-tag">
              {{ t.label }}
            </span>
          </div>
        </section>
        <p v-else class="pop-criteria__empty">사주 정보를 불러오지 못했어요. 잠시 후 다시 시도해주세요.</p>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
$naming-accent-bright: #724ce5;
$naming-tag-bg: #f8f6ff;

.pop-criteria-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 9999;
  box-sizing: border-box;
}

.pop-criteria {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  width: 100%;
  max-width: 400px;
  max-height: calc(100vh - 32px);
  padding: 15px 16px 20px;
  border-radius: 16px;
  background: #fff;
  box-sizing: border-box;
  overflow-y: auto;
}

.pop-criteria__empty {
  width: 100%;
  padding: 32px 0 24px;
  color: #757575;
  font-family: Pretendard;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}

/* 모바일: 분석 기준은 내 사주 한 섹션만이라 풀스크린 불필요 — PC와 동일한 중앙 카드 유지
   backdrop padding(16px) + max-width 400px로 좌우 안전 마진 확보 */
@media (max-width: 768px) {
  .pop-criteria {
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

/* 헤더 */
.pop-criteria__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 10px 0;
  min-height: 24px;
}

.pop-criteria__title-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.pop-criteria__title {
  margin: 0 0 0 -4px;
  color: #333;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  white-space: nowrap;
}

.pop-criteria__tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 12px;
  background: $naming-tag-bg;
  color: #453674;
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;
}

.pop-criteria__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
}

/* 섹션 */
.pop-criteria__section {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  width: 100%;
  padding: 16px;
  border-radius: 15px;
  border: 1px solid #ebebeb;
  background: #fff;
  box-sizing: border-box;
}

.pop-criteria__section-head {
  display: flex;
  align-items: center;
  gap: 3px;
}

.pop-criteria__section-title {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
}

.pop-criteria__pillars-labels {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
  text-align: center;
}

.pop-criteria__pillar-label {
  color: #575757;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
  line-height: 15px;
}

.pop-criteria__pillars {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
}

.pop-criteria__pillar {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  min-width: 0;
  padding: 8px 4px;
  border-radius: 6px;
  background: #f8f8f8;
  box-sizing: border-box;
}

.pop-criteria__pillar-chars {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  font-family: 'ChosunGs', serif;
  font-size: 22px;
  font-weight: 400;
  line-height: 20px;
}

.pop-criteria__pillar-kor {
  color: #575757;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.pop-criteria__saju-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
}

.pop-criteria__saju-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 12px;
  background: $naming-tag-bg;
  color: $naming-accent-bright;
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
}
</style>
